import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getCurrentEnv } from '../../hooks/apiUrlResolver';
import getEssentialsUrl from '../../api/config';

// ─── Constants ───────────────────────────────────────────────────────────────

const JOURNEY_ID = 'authentication';

const getThalesBaseUrl = (): string => {
  const env = getCurrentEnv();
  const suffix = window.location.host.toLowerCase().includes('.net') ? '.net' : '.com';
  if (env === 'prd') return `https://avlogin.availity${suffix}/vio/custom/`;
  if (env === 'qua') return `https://avlogin-qua.availity${suffix}/vio/custom/`;
  return `https://avlogin-tst.availity${suffix}/vio/custom/`;
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface CallbackInput { name: string; value: string }
interface CallbackOutput { name: string; value: unknown }
interface Callback { type: string; input?: CallbackInput[]; output?: CallbackOutput[] }
interface AuthStep {
  authId?: string;
  stage?: string;
  type?: string;
  callbacks?: Callback[];
  successUrl?: string;
  tokenId?: string;
  payload?: { message?: string };
}

// ─── Callback Utilities (mirrors fr-ui pattern) ─────────────────────────────

const getCallbacksByType = (callbacks: Callback[], type: string): Callback[] =>
  callbacks.filter((cb) => cb.type?.toLowerCase() === type.toLowerCase());

const getCallbackByType = (callbacks: Callback[], type: string): Callback | undefined =>
  callbacks.find((cb) => cb.type?.toLowerCase() === type.toLowerCase());

const getOutputValue = (cb: Callback, outputName: string): unknown => {
  const entry = cb.output?.find((o) => o.name?.toLowerCase() === outputName.toLowerCase());
  return entry?.value;
};

const getErrorCodeFromCallbacks = (callbacks: Callback[]): string | null => {
  const hiddenCallbacks = getCallbacksByType(callbacks, 'HiddenValueCallback');
  for (const cb of hiddenCallbacks) {
    const idOutput = cb.output?.find((o) => o.name?.toLowerCase() === 'id');
    if (idOutput?.value === 'errorCode') {
      const valueOutput = cb.output?.find((o) => o.name?.toLowerCase() === 'value');
      const code = valueOutput?.value as string;
      if (code && code !== '0') return code;
    }
  }
  return null;
};

const isDeviceProfileCallback = (step: AuthStep): boolean => {
  const { callbacks = [] } = step;
  return callbacks.some(
    (cb) =>
      cb.type?.toLowerCase() === 'deviceprofilecallback' ||
      (cb.type?.toLowerCase() === 'metadatacallback' &&
        cb.output?.some((o) => o.name === 'name' && o.value === 'deviceProfile'))
  );
};

// ─── Error Messages ──────────────────────────────────────────────────────────

const ERROR_MESSAGES: Record<string, string> = {
  '111': 'Your account has been locked due to too many failed attempts. Please contact support.',
  '149': 'Your session has expired. Please start a new login.',
  '150': 'An authentication error occurred. Please try again.',
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  container: { padding: '2rem', maxWidth: 480, margin: '0 auto', fontFamily: 'system-ui, sans-serif' },
  card: { border: '1px solid #e0e0e0', borderRadius: 8, padding: '2rem', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  title: { fontSize: '1.5rem', fontWeight: 600, marginBottom: '1.5rem', textAlign: 'center' as const },
  field: { marginBottom: '1rem' },
  label: { display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem', color: '#333' },
  input: { display: 'block', width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.95rem', boxSizing: 'border-box' as const },
  button: { width: '100%', padding: '0.7rem', background: '#0056b3', color: '#fff', border: 'none', borderRadius: 4, fontSize: '1rem', fontWeight: 500, cursor: 'pointer', marginTop: '0.5rem' },
  buttonDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  link: { color: '#0056b3', textDecoration: 'none', fontSize: '0.875rem', cursor: 'pointer' },
  error: { padding: '0.75rem', background: '#f8d7da', color: '#721c24', borderRadius: 4, marginBottom: '1rem', fontSize: '0.875rem' },
  success: { padding: '1.5rem', background: '#d4edda', color: '#155724', borderRadius: 8, textAlign: 'center' as const },
  info: { padding: '0.75rem', background: '#cce5ff', color: '#004085', borderRadius: 4, marginBottom: '1rem', fontSize: '0.875rem' },
  stage: { fontSize: '0.75rem', color: '#666', textAlign: 'center' as const, marginTop: '1rem' },
  envBar: { fontSize: '0.75rem', color: '#888', textAlign: 'center' as const, marginBottom: '1rem' },
  choiceContainer: { marginBottom: '1rem' },
  choiceOption: { display: 'flex', alignItems: 'center', marginBottom: '0.4rem', cursor: 'pointer' },
  radio: { marginRight: '0.5rem' },
};

// ─── Component ───────────────────────────────────────────────────────────────

const ThalesLoginTest = () => {
  const [authId, setAuthId] = useState<string | null>(null);
  const [step, setStep] = useState<AuthStep | null>(null);
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [logs, setLogs] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);

  const endpoint = `${getThalesBaseUrl()}${JOURNEY_ID}`;
  const envoyNavUrl = `${getEssentialsUrl()}/static/web/onb/onboarding-ui-apps/navigation/`;

  const log = useCallback((msg: string) => {
    setLogs((prev) => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
  }, []);

  // ─── Read auth_id from URL ─────────────────────────────────────────────────

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    let id = searchParams.get('auth_id');

    if (!id) {
      const { hash } = window.location;
      const hashQuery = hash.includes('?') ? hash.split('?')[1] : '';
      const hashParams = new URLSearchParams(hashQuery);
      id = hashParams.get('auth_id');
    }

    if (id) {
      setAuthId(id);
      log(`auth_id from URL: ${id}`);
    }
  }, [log]);

  // ─── API call ──────────────────────────────────────────────────────────────

  const callEndpoint = useCallback(async (body: Record<string, unknown>) => {
    setLoading(true);
    log(`POST ${JOURNEY_ID} → ${JSON.stringify(body).slice(0, 150)}...`);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await res.text();
      let data: AuthStep;
      try {
        data = JSON.parse(text);
      } catch {
        log(`Non-JSON response (${res.status}): ${text.slice(0, 200)}`);
        setLoading(false);
        return;
      }

      log(`← status=${res.status}, type=${data.type || 'step'}, stage=${data.stage || '—'}`);

      // Handle terminal states
      if (data.type === 'LoginSuccess' || data.tokenId) {
        log('✅ LOGIN SUCCESS');
        setStep(data);
        setLoading(false);
        return;
      }

      if (data.type === 'LoginFailure') {
        const msg = data.payload?.message || 'Unknown failure';
        log(`❌ LOGIN FAILED: ${msg}`);
        setStep(data);
        setLoading(false);
        return;
      }

      // Handle DeviceProfile auto-submit (no UI shown)
      if (isDeviceProfileCallback(data)) {
        log('⚙️ DeviceProfile detected — auto-submitting...');
        const deviceCb = data.callbacks?.find(
          (cb) =>
            cb.type?.toLowerCase() === 'deviceprofilecallback' ||
            cb.type?.toLowerCase() === 'metadatacallback'
        );
        if (deviceCb?.input?.[0]) {
          deviceCb.input[0].value = JSON.stringify({
            identifier: navigator.userAgent,
            metadata: {
              platform: { userAgent: navigator.userAgent, language: navigator.language },
              hardware: { display: { width: window.screen.width, height: window.screen.height } },
            },
          });
        }
        // Auto-submit device profile
        await callEndpoint({ authId: data.authId, callbacks: data.callbacks });
        return;
      }

      setStep(data);
      setFormValues({});
    } catch (error_: unknown) {
      const msg = (error_ as Error).message;
      log(`❌ Error: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [endpoint, log]);

  // ─── Auto-start login flow ─────────────────────────────────────────────────

  useEffect(() => {
    if (authId && !step && !loading) {
      callEndpoint({ output: 'next', authId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authId]);

  // ─── Submit step ───────────────────────────────────────────────────────────

  const submitStep = useCallback((overrideCallbacks?: Callback[]) => {
    if (!step?.authId) return;
    const callbacks = overrideCallbacks || step.callbacks || [];
    callEndpoint({ authId: step.authId, callbacks });
  }, [step, callEndpoint]);

  // ─── Stage-specific submit handlers ────────────────────────────────────────

  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!step?.callbacks) return;

    const callbacks = step.callbacks.map((cb) => {
      if (cb.type === 'NameCallback' && cb.input?.[0]) {
        return { ...cb, input: [{ ...cb.input[0], value: formValues.userId || '' }] };
      }
      if (cb.type === 'PasswordCallback' && cb.input?.[0]) {
        return { ...cb, input: [{ ...cb.input[0], value: formValues.password || '' }] };
      }
      return cb;
    });

    submitStep(callbacks);
  };

  const handleChoiceSubmit = (choiceIndex: number) => {
    if (!step?.callbacks) return;

    const callbacks = step.callbacks.map((cb) => {
      if (cb.type === 'ChoiceCallback' && cb.input?.[0]) {
        return { ...cb, input: [{ ...cb.input[0], value: String(choiceIndex) }] };
      }
      return cb;
    });

    submitStep(callbacks);
  };

  const handleTokenSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!step?.callbacks) return;

    const callbacks = step.callbacks.map((cb) => {
      if (cb.type === 'TextInputCallback' && cb.input?.[0]) {
        return { ...cb, input: [{ ...cb.input[0], value: formValues.token || '' }] };
      }
      return cb;
    });

    submitStep(callbacks);
  };

  const handleConfirmationSubmit = (optionIndex: number) => {
    if (!step?.callbacks) return;

    const callbacks = step.callbacks.map((cb) => {
      if (cb.type === 'ConfirmationCallback' && cb.input?.[0]) {
        return { ...cb, input: [{ ...cb.input[0], value: String(optionIndex) }] };
      }
      return cb;
    });

    submitStep(callbacks);
  };

  const handlePasswordResetSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!step?.callbacks) return;

    const passwordCallbacks = step.callbacks.filter((cb) => cb.type === 'PasswordCallback');
    const callbacks = step.callbacks.map((cb) => {
      if (cb.type === 'PasswordCallback' && cb.input?.[0]) {
        const idx = passwordCallbacks.indexOf(cb);
        const val = idx === 0 ? formValues.newPassword || '' : formValues.confirmPassword || '';
        return { ...cb, input: [{ ...cb.input[0], value: val }] };
      }
      return cb;
    });

    submitStep(callbacks);
  };

  // ─── Error code from current step ─────────────────────────────────────────

  const currentErrorCode = useMemo(() => {
    if (!step?.callbacks) return null;
    return getErrorCodeFromCallbacks(step.callbacks);
  }, [step]);

  const errorMessage = useMemo(() => {
    if (!currentErrorCode) return null;
    return ERROR_MESSAGES[currentErrorCode] || `Error code: ${currentErrorCode}`;
  }, [currentErrorCode]);

  // ─── Render: No auth_id yet ────────────────────────────────────────────────

  if (!authId && !step) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.title}>Thales Login Test</h2>
          <div style={styles.envBar}>
            Env: <strong>{getCurrentEnv()}</strong> | Endpoint: {endpoint}
          </div>
          <div style={{ ...styles.info, marginTop: '1rem' }}>
            <strong>No auth_id found.</strong> The login flow requires an auth_id from Envoy/OneWelcome.
          </div>
          <p style={{ fontSize: '0.85rem', color: '#555', marginBottom: '1rem' }}>
            Click below to redirect to Envoy. It will create an auth_id and redirect back.
            On localhost this may redirect to fr-ui instead — use the real tst domain for end-to-end.
          </p>
          <button
            type="button"
            onClick={() => { log(`Redirecting to Envoy: ${envoyNavUrl}`); window.location.href = envoyNavUrl; }}
            style={styles.button}
          >
            Get auth_id from Envoy
          </button>
          <p style={{ fontSize: '0.75rem', color: '#999', marginTop: '1rem', textAlign: 'center' }}>
            Or navigate directly with <code>?auth_id=YOUR_ID</code>
          </p>
        </div>
      </div>
    );
  }

  // ─── Render: Login Success ─────────────────────────────────────────────────

  if (step?.type === 'LoginSuccess' || step?.tokenId) {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.card, ...styles.success }}>
          <h2>✅ Login Successful</h2>
          {step.successUrl && (
            <>
              <p style={{ marginTop: '1rem' }}>Redirecting to complete session setup...</p>
              <a href={step.successUrl} style={{ ...styles.link, fontWeight: 600 }}>
                Click here if not redirected
              </a>
            </>
          )}
        </div>
        <details style={{ marginTop: '1rem' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.8rem', color: '#666' }}>Raw Response</summary>
          <pre style={{ fontSize: '0.75rem', background: '#f5f5f5', padding: '0.5rem', overflow: 'auto', maxHeight: 200 }}>
            {JSON.stringify(step, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  // ─── Render: Login Failure ─────────────────────────────────────────────────

  if (step?.type === 'LoginFailure') {
    const failureCode = step.payload?.message || '';
    const failureMsg = ERROR_MESSAGES[failureCode] || step.payload?.message || 'Login failed. Please try again.';

    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={{ ...styles.title, color: '#721c24' }}>Login Failed</h2>
          <div style={styles.error}>{failureMsg}</div>
          <button
            type="button"
            onClick={() => { setStep(null); setAuthId(null); setFormValues({}); }}
            style={styles.button}
          >
            Start Over
          </button>
        </div>
      </div>
    );
  }

  // ─── Render: Stage-based UI ────────────────────────────────────────────────

  const renderStageContent = () => {
    if (!step) return null;
    const { stage = '', callbacks = [] } = step;

    // ── LoginpageUnique001: Username + Password ─────────────────────────────
    if (stage === 'LoginpageUnique001') {
      return (
        <form onSubmit={handleLoginSubmit}>
          <h2 style={styles.title}>Sign In</h2>
          {errorMessage && <div style={styles.error}>{errorMessage}</div>}
          <div style={styles.field}>
            <label style={styles.label} htmlFor="userId">User ID</label>
            <input
              id="userId"
              type="text"
              style={styles.input}
              placeholder="Enter your user ID"
              value={formValues.userId || ''}
              onChange={(e) => setFormValues({ ...formValues, userId: e.target.value })}
              autoComplete="username"
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="password">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                style={styles.input}
                placeholder="Enter your password"
                value={formValues.password || ''}
                onChange={(e) => setFormValues({ ...formValues, password: e.target.value })}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#666' }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          <button type="submit" style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }} disabled={loading}>
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
      );
    }

    // ── Terms of Service ────────────────────────────────────────────────────
    if (stage === 'Login-ShowTermsOfService') {
      const textOutputCb = getCallbackByType(callbacks, 'TextOutputCallback');
      const tosContent = textOutputCb ? (getOutputValue(textOutputCb, 'message') as string) : '';
      const confirmCb = getCallbackByType(callbacks, 'ConfirmationCallback');
      const options = (confirmCb ? getOutputValue(confirmCb, 'options') : ['Accept', 'Decline']) as string[];

      return (
        <div>
          <h2 style={styles.title}>Terms of Service</h2>
          {tosContent && (
            <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', padding: '0.75rem', borderRadius: 4, marginBottom: '1rem', fontSize: '0.85rem' }}>
              {tosContent}
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {options.map((opt, idx) => (
              <button
                key={opt}
                type="button"
                onClick={() => handleConfirmationSubmit(idx)}
                style={{ ...styles.button, width: 'auto', flex: 1, background: idx === 0 ? '#0056b3' : '#dc3545' }}
                disabled={loading}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      );
    }

    // ── MFA Choose Method ───────────────────────────────────────────────────
    if (stage === 'MFA-SelectMethod' || stage === 'MFAEnrollment-SelectMethod') {
      const choiceCb = getCallbackByType(callbacks, 'ChoiceCallback');
      const choices = (choiceCb ? getOutputValue(choiceCb, 'choices') : []) as string[];
      const prompt = choiceCb ? (getOutputValue(choiceCb, 'prompt') as string) : 'Select verification method';

      return (
        <div>
          <h2 style={styles.title}>2-Step Verification</h2>
          <p style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>{prompt}</p>
          <div style={styles.choiceContainer}>
            {choices.map((choice, idx) => (
              <label key={choice} style={styles.choiceOption}>
                <input
                  type="radio"
                  name="mfaMethod"
                  value={idx}
                  checked={formValues.choice === String(idx)}
                  onChange={() => setFormValues({ ...formValues, choice: String(idx) })}
                  style={styles.radio}
                />
                {choice}
              </label>
            ))}
          </div>
          <button
            type="button"
            onClick={() => handleChoiceSubmit(Number(formValues.choice || '0'))}
            style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Continue'}
          </button>
        </div>
      );
    }

    // ── MFA Welcome ─────────────────────────────────────────────────────────
    if (stage === 'MFAEnrollment-Welcome') {
      const confirmCb = getCallbackByType(callbacks, 'ConfirmationCallback');
      const options = (confirmCb ? getOutputValue(confirmCb, 'options') : ['Continue']) as string[];

      return (
        <div>
          <h2 style={styles.title}>Set Up 2-Step Authentication</h2>
          <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
            For your security, we require 2-step authentication. Choose a method to verify your identity when signing in.
          </p>
          {options.map((opt, idx) => (
            <button
              key={opt}
              type="button"
              onClick={() => handleConfirmationSubmit(idx)}
              style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
              disabled={loading}
            >
              {opt}
            </button>
          ))}
        </div>
      );
    }

    // ── MFA OTP/Token Input (SMS, Voice, Authenticator) ─────────────────────
    if (
      stage.includes('SMSOTPVerify') ||
      stage.includes('TTSOTPVerify') ||
      stage.includes('AppOATHVerify')
    ) {
      // The code input can be either TextInputCallback or NameCallback depending on the stage
      const textInputCb = getCallbackByType(callbacks, 'TextInputCallback');
      const nameInputCb = getCallbackByType(callbacks, 'NameCallback');
      const codeCallback = textInputCb || nameInputCb;
      const prompt = codeCallback ? (getOutputValue(codeCallback, 'prompt') as string) : 'Enter verification code';
      const isAuthenticator = stage.includes('AppOATH');

      const handleOtpSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!step?.callbacks) return;

        const updatedCallbacks = step.callbacks.map((cb) => {
          // Fill the code into whichever callback type holds it
          if ((cb.type === 'TextInputCallback' || cb.type === 'NameCallback') && cb.input?.[0]) {
            return { ...cb, input: [{ ...cb.input[0], value: formValues.token || '' }] };
          }
          // Keep ConfirmationCallback as-is (default 0 = Submit)
          return cb;
        });

        submitStep(updatedCallbacks);
      };

      return (
        <form onSubmit={handleOtpSubmit}>
          <h2 style={styles.title}>{isAuthenticator ? 'Authenticator Code' : 'Verification Code'}</h2>
          {errorMessage && <div style={styles.error}>{errorMessage}</div>}
          <p style={{ fontSize: '0.85rem', marginBottom: '1rem', color: '#555' }}>
            {isAuthenticator
              ? 'Enter the code from your authenticator app.'
              : 'Enter the code sent to your device.'}
          </p>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="token">{prompt}</label>
            <input
              id="token"
              type="text"
              style={styles.input}
              placeholder="000000"
              value={formValues.token || ''}
              onChange={(e) => setFormValues({ ...formValues, token: e.target.value })}
              autoComplete="one-time-code"
              required
            />
          </div>
          <button type="submit" style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }} disabled={loading}>
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>
      );
    }

    // ── MFA TOTP Setup (QR Code) ────────────────────────────────────────────
    if (stage === 'MFAEnrollment-TOTPSetup' || stage === 'MFAEnrollment-OATHAppRegister') {
      const textOutputCb = getCallbackByType(callbacks, 'TextOutputCallback');
      const qrUri = textOutputCb ? (getOutputValue(textOutputCb, 'message') as string) : '';
      const secret = qrUri?.includes('secret=') ? qrUri.split('secret=')[1]?.split('&')[0] : '';
      const textInputCb = getCallbackByType(callbacks, 'TextInputCallback');
      const prompt = textInputCb ? (getOutputValue(textInputCb, 'prompt') as string) : 'Enter verification code';

      return (
        <form onSubmit={handleTokenSubmit}>
          <h2 style={styles.title}>Set Up Authenticator App</h2>
          {errorMessage && <div style={styles.error}>{errorMessage}</div>}
          <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            Scan the QR code below with your authenticator app, or enter the secret key manually:
          </p>
          {secret && (
            <div style={{ background: '#f5f5f5', padding: '0.5rem', borderRadius: 4, marginBottom: '1rem', fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all', textAlign: 'center' }}>
              {secret}
            </div>
          )}
          {qrUri && (
            <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '1rem', wordBreak: 'break-all' }}>
              URI: {qrUri}
            </div>
          )}
          <div style={styles.field}>
            <label style={styles.label} htmlFor="token">{prompt}</label>
            <input
              id="token"
              type="text"
              style={styles.input}
              placeholder="000000"
              value={formValues.token || ''}
              onChange={(e) => setFormValues({ ...formValues, token: e.target.value })}
              required
            />
          </div>
          <button type="submit" style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }} disabled={loading}>
            {loading ? 'Verifying...' : 'Verify Code'}
          </button>
        </form>
      );
    }

    // ── MFA Complete (with/without backup codes) ────────────────────────────
    if (stage.includes('Complete') && (stage.includes('MFA') || stage.includes('confirmation'))) {
      const textOutputCb = getCallbackByType(callbacks, 'TextOutputCallback');
      let backupCodes: string[] = [];
      if (textOutputCb) {
        try {
          const msg = getOutputValue(textOutputCb, 'message') as string;
          const parsed = JSON.parse(msg);
          backupCodes = parsed.backupCodes || [];
        } catch { /* not JSON or no codes */ }
      }
      const confirmCb = getCallbackByType(callbacks, 'ConfirmationCallback');
      const options = (confirmCb ? getOutputValue(confirmCb, 'options') : ['Done']) as string[];

      return (
        <div>
          <h2 style={styles.title}>2-Step Setup Complete</h2>
          {backupCodes.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                Save your backup codes in a safe place:
              </p>
              <div style={{ background: '#f5f5f5', padding: '0.75rem', borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                {backupCodes.map((code) => (
                  <div key={code}>{code}</div>
                ))}
              </div>
            </div>
          )}
          {options.map((opt, idx) => (
            <button
              key={opt}
              type="button"
              onClick={() => handleConfirmationSubmit(idx)}
              style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
              disabled={loading}
            >
              {opt}
            </button>
          ))}
        </div>
      );
    }

    // ── MFA Backup Code Input ───────────────────────────────────────────────
    if (stage === 'MFA-BackupVerify' || stage === 'MFAEnrollment-BackupVerify') {
      const textInputCb = getCallbackByType(callbacks, 'TextInputCallback');
      const prompt = textInputCb ? (getOutputValue(textInputCb, 'prompt') as string) : 'Enter backup code';

      return (
        <form onSubmit={handleTokenSubmit}>
          <h2 style={styles.title}>Backup Code</h2>
          {errorMessage && <div style={styles.error}>{errorMessage}</div>}
          <div style={styles.field}>
            <label style={styles.label} htmlFor="token">{prompt}</label>
            <input
              id="token"
              type="text"
              style={styles.input}
              placeholder="Enter your backup code"
              value={formValues.token || ''}
              onChange={(e) => setFormValues({ ...formValues, token: e.target.value })}
              required
            />
          </div>
          <button type="submit" style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }} disabled={loading}>
            {loading ? 'Verifying...' : 'Submit'}
          </button>
        </form>
      );
    }

    // ── Password Expiring ───────────────────────────────────────────────────
    if (stage === 'LoginExpiringPassword') {
      const confirmCb = getCallbackByType(callbacks, 'ConfirmationCallback');
      const options = (confirmCb ? getOutputValue(confirmCb, 'options') : ['Change Now', 'Remind Me Later']) as string[];

      return (
        <div>
          <h2 style={styles.title}>Password Expiring Soon</h2>
          <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
            Your password will expire soon. Would you like to change it now?
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {options.map((opt, idx) => (
              <button
                key={opt}
                type="button"
                onClick={() => handleConfirmationSubmit(idx)}
                style={{ ...styles.button, flex: 1 }}
                disabled={loading}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      );
    }

    // ── Password Reset ──────────────────────────────────────────────────────
    if (stage === 'ForgottenPassword002') {
      return (
        <form onSubmit={handlePasswordResetSubmit}>
          <h2 style={styles.title}>Reset Password</h2>
          {errorMessage && <div style={styles.error}>{errorMessage}</div>}
          <div style={styles.field}>
            <label style={styles.label} htmlFor="newPassword">New Password</label>
            <input
              id="newPassword"
              type="password"
              style={styles.input}
              value={formValues.newPassword || ''}
              onChange={(e) => setFormValues({ ...formValues, newPassword: e.target.value })}
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              style={styles.input}
              value={formValues.confirmPassword || ''}
              onChange={(e) => setFormValues({ ...formValues, confirmPassword: e.target.value })}
              required
            />
          </div>
          <button type="submit" style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }} disabled={loading}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      );
    }

    // ── Password Reset Success ──────────────────────────────────────────────
    if (stage === 'ForgottenPassword003') {
      const confirmCb = getCallbackByType(callbacks, 'ConfirmationCallback');
      const options = (confirmCb ? getOutputValue(confirmCb, 'options') : ['Continue']) as string[];

      return (
        <div>
          <h2 style={styles.title}>Password Changed</h2>
          <div style={styles.info}>Your password has been successfully changed.</div>
          {options.map((opt, idx) => (
            <button
              key={opt}
              type="button"
              onClick={() => handleConfirmationSubmit(idx)}
              style={styles.button}
              disabled={loading}
            >
              {opt}
            </button>
          ))}
        </div>
      );
    }

    // ── Email Verification Stages ───────────────────────────────────────────
    if (stage === 'unverifiedemail' || stage === 'emailsent' || stage === 'emailverified' || stage === 'noemail' || stage === 'changeemail') {
      const stageMessages: Record<string, string> = {
        unverifiedemail: 'Your email address has not been verified. Please verify to continue.',
        emailsent: 'A verification email has been sent. Please check your inbox.',
        emailverified: 'Your email has been verified!',
        noemail: 'No email address on file. Please enter one to continue.',
        changeemail: 'Enter your new email address.',
      };

      const hasTextInput = getCallbackByType(callbacks, 'TextInputCallback');
      const hasConfirmation = getCallbackByType(callbacks, 'ConfirmationCallback');
      const confirmOptions = hasConfirmation
        ? (getOutputValue(hasConfirmation, 'options') as string[]) || ['Continue']
        : ['Continue'];

      return (
        <div>
          <h2 style={styles.title}>Email Verification</h2>
          <div style={styles.info}>{stageMessages[stage] || stage}</div>

          {hasTextInput && (
            <form onSubmit={handleTokenSubmit}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor="token">
                  {(getOutputValue(hasTextInput, 'prompt') as string) || 'Email Address'}
                </label>
                <input
                  id="token"
                  type="email"
                  style={styles.input}
                  value={formValues.token || ''}
                  onChange={(e) => setFormValues({ ...formValues, token: e.target.value })}
                  required
                />
              </div>
              <button type="submit" style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }} disabled={loading}>
                Submit
              </button>
            </form>
          )}

          {!hasTextInput && confirmOptions.map((opt, idx) => (
            <button
              key={opt}
              type="button"
              onClick={() => handleConfirmationSubmit(idx)}
              style={styles.button}
              disabled={loading}
            >
              {opt}
            </button>
          ))}
        </div>
      );
    }

    // ── Error Page ──────────────────────────────────────────────────────────
    if (stage === 'ErrorPage') {
      return (
        <div>
          <h2 style={{ ...styles.title, color: '#721c24' }}>Error</h2>
          <div style={styles.error}>
            {errorMessage || 'An unexpected error occurred. Please try again.'}
          </div>
          <button
            type="button"
            onClick={() => { setStep(null); setAuthId(null); setFormValues({}); }}
            style={styles.button}
          >
            Start Over
          </button>
        </div>
      );
    }

    // ── Fallback: Generic stage handler ─────────────────────────────────────
    // For any unrecognized stage, render inputs based on callback types
    const visibleCallbacks = callbacks.filter(
      (cb) =>
        cb.type !== 'HiddenValueCallback' &&
        cb.type !== 'MetadataCallback' &&
        cb.type !== 'TextOutputCallback' &&
        cb.input &&
        cb.input.length > 0
    );

    if (visibleCallbacks.length === 0) {
      // Might be a confirmation-only stage
      const confirmCb = getCallbackByType(callbacks, 'ConfirmationCallback');
      if (confirmCb) {
        const options = (getOutputValue(confirmCb, 'options') as string[]) || ['Continue'];
        return (
          <div>
            <h2 style={styles.title}>{stage}</h2>
            {options.map((opt, idx) => (
              <button key={opt} type="button" onClick={() => handleConfirmationSubmit(idx)} style={styles.button} disabled={loading}>
                {opt}
              </button>
            ))}
          </div>
        );
      }

      return (
        <div>
          <h2 style={styles.title}>{stage}</h2>
          <div style={styles.info}>This stage has no visible inputs. Check the raw response below.</div>
        </div>
      );
    }

    // Generic form for unknown stages with inputs
    return (
      <form onSubmit={(e) => { e.preventDefault(); submitStep(); }}>
        <h2 style={styles.title}>{stage}</h2>
        {errorMessage && <div style={styles.error}>{errorMessage}</div>}
        {visibleCallbacks.map((cb, idx) => {
          const prompt = (getOutputValue(cb, 'prompt') as string) || cb.type;
          const inputName = cb.input?.[0]?.name || `field-${idx}`;
          const isPassword = cb.type === 'PasswordCallback';

          return (
            <div key={inputName} style={styles.field}>
              <label style={styles.label} htmlFor={inputName}>{prompt}</label>
              <input
                id={inputName}
                type={isPassword ? 'password' : 'text'}
                style={styles.input}
                value={formValues[inputName] || ''}
                onChange={(e) => setFormValues({ ...formValues, [inputName]: e.target.value })}
              />
            </div>
          );
        })}
        <button type="submit" style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }} disabled={loading}>
          {loading ? 'Submitting...' : 'Continue'}
        </button>
      </form>
    );
  };

  // ─── Main Render ───────────────────────────────────────────────────────────

  return (
    <div style={styles.container}>
      <div style={styles.envBar}>
        Env: <strong>{getCurrentEnv()}</strong> | {authId && <>auth_id: <code>{authId.slice(0, 12)}...</code></>}
      </div>

      <div style={styles.card}>
        {loading && !step && <p style={{ textAlign: 'center', color: '#666' }}>Loading...</p>}
        {renderStageContent()}
      </div>

      {step?.stage && (
        <div style={styles.stage}>
          Stage: <code>{step.stage}</code>
        </div>
      )}

      <details style={{ marginTop: '1.5rem' }}>
        <summary style={{ cursor: 'pointer', fontSize: '0.8rem', color: '#666' }}>
          Request Log ({logs.length})
        </summary>
        <pre style={{ maxHeight: 200, overflow: 'auto', fontSize: '0.75rem', background: '#f5f5f5', padding: '0.5rem' }}>
          {logs.join('\n')}
        </pre>
      </details>

      {step && (
        <details style={{ marginTop: '0.5rem' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.8rem', color: '#666' }}>Raw Response</summary>
          <pre style={{ maxHeight: 200, overflow: 'auto', fontSize: '0.75rem', background: '#f5f5f5', padding: '0.5rem' }}>
            {JSON.stringify(step, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};

export default ThalesLoginTest;
